import { formatRecordingTimestamp, type TimeRange } from './time-range.js';

interface BaseFactSource {
  text: string;
  sourceConfidence?: number;
}

export interface PageFactSource extends BaseFactSource {
  pages: string;
  timeRange?: never;
}

export interface AudioFactSource extends BaseFactSource {
  timeRange: TimeRange;
  pages?: never;
}

export type FactSource = PageFactSource | AudioFactSource;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const normalizeTimeRange = (value: unknown): TimeRange | null => {
  if (
    !isRecord(value) ||
    !isFiniteNumber(value.startSeconds) ||
    !isFiniteNumber(value.durationSeconds)
  ) {
    return null;
  }

  return { startSeconds: value.startSeconds, durationSeconds: value.durationSeconds };
};

/** Validate an unknown persisted/API value as a canonical fact source. */
export const normalizeFactSource = (source: unknown): FactSource | null => {
  if (
    !isRecord(source) ||
    typeof source.text !== 'string' ||
    (source.sourceConfidence !== undefined && !isFiniteNumber(source.sourceConfidence))
  ) {
    return null;
  }

  const base = {
    text: source.text,
    ...(source.sourceConfidence === undefined ? {} : { sourceConfidence: source.sourceConfidence }),
  };

  if (typeof source.pages === 'string' && source.timeRange === undefined) {
    return { ...base, pages: source.pages };
  }

  const timeRange = normalizeTimeRange(source.timeRange);
  if (source.pages === undefined && timeRange) {
    return { ...base, timeRange };
  }

  return null;
};

const firstPage = (pages: string): number => {
  const match = pages.match(/\d+/);
  return match ? Number(match[0]) : Number.POSITIVE_INFINITY;
};

const sourceSortPosition = (source: FactSource): number =>
  'timeRange' in source && source.timeRange
    ? source.timeRange.startSeconds
    : firstPage(source.pages);

export const sortFactSources = (sources: readonly unknown[] | null | undefined): FactSource[] =>
  (sources ?? [])
    .map(normalizeFactSource)
    .filter((source): source is FactSource => source !== null)
    .map((source, index) => ({ source, index }))
    .sort(
      (a, b) => sourceSortPosition(a.source) - sourceSortPosition(b.source) || a.index - b.index,
    )
    .map(({ source }) => source);

export const formatFactSourceReference = (source: FactSource): string => {
  if ('pages' in source && source.pages !== undefined) {
    return `Page${source.pages.includes(',') ? 's' : ''} ${source.pages}`;
  }
  const start = source.timeRange.startSeconds;
  const end = start + source.timeRange.durationSeconds;
  return `Timestamp ${formatRecordingTimestamp(start)}–${formatRecordingTimestamp(end)}`;
};

export const formatFactSourceText = (source: FactSource): string =>
  `${source.text} [${formatFactSourceReference(source)}]`;
